import { type ApolloError } from '@apollo/client';
import {
  RegistrationType,
  useGetAvailableQSetsByExamEventIdQuery,
  useManuallyAssignedExamEventCoursesMutation,
} from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormButton,
  Form,
  Loading,
  Alert,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { EditQSetIdNumberForm, ExamRoundInformation } from '../../components';

import { createExamSchema, type EditQSetIdNumberFormSchemaType } from './schema';

export interface EditQSetIdNumberModalProps {
  open: boolean;
  onClose: () => void;
  examEventId: string;
}

export default function EditQSetIdNumberModal(props: Readonly<EditQSetIdNumberModalProps>) {
  const { open, onClose, examEventId } = props;

  const [error, setError] = useState<ApolloError | null>(null);

  const { data: availableQSet, loading: isQSetLoading } = useGetAvailableQSetsByExamEventIdQuery({
    variables: { input: { examEventId } },
    skip: !open,
  });

  const examEventCourses =
    availableQSet?.ecGetAvailableQSetsByExamEventId?.examEventCoursesWithAssignableQSets ?? [];

  const examEvent = availableQSet?.ecGetAvailableQSetsByExamEventId?.examEvent;

  const data = {
    examEvent: {
      examSessions:
        examEvent?.sessions.map((session) => ({
          id: session.id,
          startedAt: session.startedAt,
          endedAt: session.endedAt,
          round: session.round,
        })) ?? [],
    },
    examEventCourses: [...examEventCourses]
      .sort((a, b) => {
        const aId = a.examEventCourse.examCourse?.examId ?? 0;
        const bId = b.examEventCourse.examCourse?.examId ?? 0;
        return aId - bId;
      })
      .map((examEventCourse) => {
        return {
          id: examEventCourse.examEventCourse.id,
          examCourse: {
            code: examEventCourse.examEventCourse.examCourse?.code || '',
            status: examEventCourse.examEventCourse.status,
            examId: examEventCourse.examEventCourse.examCourse?.examId || 0,
            settings: examEventCourse.examEventCourse.examCourse?.settings || {},
          },
          options: [
            { value: '-', label: '-' },
            ...[...examEventCourse.availableQSets]
              .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
              .map((qSet) => ({
                value: qSet.id,
                label: qSet.code.split('-').pop() || '',
              })),
          ],
          value: examEventCourse.examEventCourse.qSet?.id || '-',
        };
      }),
  };

  const [manuallyAssignQSetIdNumber, { loading: isAssigningQSetIdNumber }] =
    useManuallyAssignedExamEventCoursesMutation();

  const { showSnackbar } = useSnackbar();

  const schema = createExamSchema(data.examEventCourses);

  const form = useForm<EditQSetIdNumberFormSchemaType>({
    resolver: zodResolver(schema),
    defaultValues:
      availableQSet?.ecGetAvailableQSetsByExamEventId?.examEventCoursesWithAssignableQSets?.reduce(
        (acc, examEventCourse) => {
          const fieldName = examEventCourse.examEventCourse.id || '';
          const fieldValue = examEventCourse.examEventCourse.qSet?.id || '-';

          acc[fieldName] = fieldValue;

          return acc;
        },
        {} as { [x: string]: string },
      ),
  });

  const onSubmit = (data: EditQSetIdNumberFormSchemaType) => {
    const assignedExamEventCourses = Object.entries(data).map(([courseId, qSetId]) => {
      return {
        examEventCourseId: courseId,
        qSetId: qSetId === '-' ? null : qSetId,
      };
    });
    manuallyAssignQSetIdNumber({
      variables: {
        input: {
          examEventId,
          assignedExamEventCourses,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'แก้ไขชุดข้อสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: (error) => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการแก้ไขชุดข้อสอบ',
          variant: 'error',
        });
        setError(error);
      },
      refetchQueries: ['getAvailableQSetsByExamEventId', 'getExamEventCoursesByExamEventIds'],
    });
  };

  const handleClose = () => {
    form.reset();
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg'>
      <DialogTitle onClose={onClose}>แก้ไขชุดข้อสอบ</DialogTitle>
      <Divider />
      {isQSetLoading ? (
        <DialogContent>
          <Loading />
        </DialogContent>
      ) : (
        <FormProvider {...form}>
          <Form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogContent
              sx={(theme) => ({ gap: theme.spacing(6), display: 'flex', flexDirection: 'column' })}
            >
              {error ? (
                <Alert severity='error'>
                  ไม่สามารถบันทึกได้ ชุดข้อสอบที่เลือกถูกปิดใช้งานแล้ว กรุณาเลือกชุดข้อสอบใหม่
                </Alert>
              ) : null}
              <ExamRoundInformation
                examCenterCode={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.examCenterCode ?? ''
                }
                examStartedAt={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.examStartedAt ??
                  new Date()
                }
                examEndedAt={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.examEndedAt ??
                  new Date()
                }
                examRoundNumber={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.roundNumber ?? 1
                }
                examLocation={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.examLocation.nameTh ??
                  ''
                }
                province={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.examLocation.province
                    .nameTh ?? ''
                }
                registrationType={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.registrationType ??
                  RegistrationType.InHouse
                }
                examSessions={
                  availableQSet?.ecGetAvailableQSetsByExamEventId.examEvent.sessions.map(
                    (session) => ({
                      id: session.id,
                      startedAt: session.startedAt,
                      endedAt: session.endedAt,
                      round: session.round,
                    }),
                  ) ?? []
                }
              />
              <EditQSetIdNumberForm data={data} />
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
                ยกเลิก
              </Button>
              <FormButton label='บันทึก' size='large' loading={isAssigningQSetIdNumber} />
            </DialogActions>
          </Form>
        </FormProvider>
      )}
    </Dialog>
  );
}
